import {useEffect, useState} from "react";
import {Button} from "react-bootstrap";
import {appAxiosInstance} from "../../../api";
import {toast} from "react-toastify";
import {PaginationControl} from "react-bootstrap-pagination-control";
import s from './logList.module.css';
import LogsFittingItemList from "../LogsFittingItemList/LogsFittingItemList";
import LogsRecommendItemList from "../LogsRecommendItemList";
import LogsFeedItemList from "../LogsFeedItemList/LogsFeedItemList";
import { Loader } from "../../../components/Loader/loader";

interface IUserRequest {
    requestDate: string;
    userName: string;
    infoRequestAssistant: string;
    infoInClothesMs: string;
    filteredClothes: string;
    infoInBackFromRecommendation: string;
    collagesInfo: string;
}

interface IClothesInfo {
    clothId: number,
    variantId: number,
    name: string,
    thumbUrl: string,
    modelLink: string,
    color: string
}

interface IDressingRequest {
    logId: string,
    userEmail: string,
    userId: number,
    dateStartFitting: string,
    dateEndFitting: string,
    dateSendResults: string | null,
    linkOriginalPhoto: string | null,
    roomGuid: string | null,
    fittingPhotoUrl: string | null,
    fashionGuid: string,
    clothes: IClothesInfo[]
}

interface IFeedItem {
    logId: string;
    userEmail: string;
    userId: number;
    userName: string | null;
    collageUrl: string;
    styleName: string;
    styleId: number;
    fashionGuid: string;
    startView: string;
    endView: string;
    viewingDuration: number;
}

interface ILogsRequestParams {
    currentPage: number;
    itemsPerPage: number;
    dateBegin: string | null;
    dateEnd: string | null;
    withEmployees: boolean;
}

interface ILogResponse {
    countRecords: number;
    userRequests: IUserRequest[];
}

interface ILogDressingResponse {
    countRecords: number;
    fittingLogsItem: IDressingRequest[];
}

interface ILogFeedResponse {
    countRecords: number;
    items: IFeedItem[];
}

export default function LogsList() {
    const [logsRecommendation, setLogsRecommendation] = useState<IUserRequest[]>([]);
    const [logsFitting, setLogsFitting] = useState<IDressingRequest[]>([]);
    const [logsFeed, setLogsFeed] = useState<IFeedItem[]>([]);
    const [itemsPerPage, setItemsPerPage] = useState(12);
    const [currentPage, setCurrentPage] = useState(1);
    const [allRecommendationItems, setAllRecommendationItems] = useState(0);
    const [allFittingItems, setAllFittingItems] = useState(0);
    const [allFeedItems, setAllFeedItems] = useState(0);
    const [dateBegin, setDateBegin] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [activeTab, setActiveTab] = useState<'recommendation' | 'fitting' | 'feed'>('feed');
    const [isLoading, setIsLoading] = useState(false);
    const [withEmployees, setWithEmployees] = useState<boolean>(true);

    const getLogsRecommendation = async () => {
        setIsLoading(true);
        try {
            const params: ILogsRequestParams = {
                currentPage: currentPage,
                itemsPerPage: itemsPerPage,
                dateBegin: dateBegin || null,
                dateEnd: dateEnd || null,
                withEmployees: withEmployees
            }

            const logsResponse = await appAxiosInstance.get<ILogResponse>('logs', {params});
            const logsList = logsResponse.data;

            if (logsList?.userRequests) {
                setLogsRecommendation(logsList.userRequests);
                setAllRecommendationItems(logsList.countRecords || 0);
            } else {
                setLogsRecommendation([]);
                setAllRecommendationItems(0);
            }
        } catch (error) {
            toast.error(`Ошибка при получении логов рекомендаций`);
            setLogsRecommendation([]);
            setAllRecommendationItems(0);
        } finally {
            setIsLoading(false);
        }
    };

    const getLogsFitting = async () => {
        setIsLoading(true);
        try {
            const params: ILogsRequestParams = {
                currentPage: currentPage,
                itemsPerPage: itemsPerPage,
                dateBegin: dateBegin || null,
                dateEnd: dateEnd || null,
                withEmployees: withEmployees
            }

            const logsResponse = await appAxiosInstance.get<ILogDressingResponse>('logs/fitting', {params});
            const logsList = logsResponse.data;

            if (logsList?.fittingLogsItem) {
                setLogsFitting(logsList.fittingLogsItem);
                setAllFittingItems(logsList.countRecords || 0);
            } else {
                setLogsFitting([]);
                setAllFittingItems(0);
            }
        } catch (error) {
            toast.error(`Ошибка при получении логов переодевалки`);
            setLogsFitting([]);
            setAllFittingItems(0);
        } finally {
            setIsLoading(false);
        }
    };

    const getLogsFeed = async () => {
        setIsLoading(true);
        try {
            const params: ILogsRequestParams = {
                currentPage: currentPage,
                itemsPerPage: 12,
                dateBegin: dateBegin || null,
                dateEnd: dateEnd || null,
                withEmployees: withEmployees
            }

            const logsResponse = await appAxiosInstance.get<ILogFeedResponse>('logs/viewCollages', {params});
            const logsList = logsResponse.data;

            if (logsList?.items) {
                setLogsFeed(logsList.items);
                setAllFeedItems(logsList.countRecords || 0);
            } else {
                setLogsFeed([]);
                setAllFeedItems(0);
            }
        } catch (error) {
            toast.error(`Ошибка при получении ленты`);
            setLogsFeed([]);
            setAllFeedItems(0);
        } finally {
            setIsLoading(false);
        }
    };

    const paginate = (pageNumbers: number) => {
        setCurrentPage(pageNumbers);
    }

    const find = () => {
        if (activeTab === 'recommendation') {
            getLogsRecommendation();
        } else if (activeTab === 'fitting') {
            getLogsFitting();
        } else if (activeTab === 'feed') {
            getLogsFeed();
        }
    }

    const cleanForm = () => {
        setDateBegin('');
        setDateEnd('');
    }

    const handleTabChange = (tab: 'recommendation' | 'fitting' | 'feed') => {
        setActiveTab(tab);
        setCurrentPage(1);
    }

    const handleArchiveToggle = (state: boolean) => {
        setWithEmployees(state);
    };

    useEffect(() => {
        if (activeTab === 'recommendation') {
            getLogsRecommendation();
        } else if (activeTab === 'fitting') {
            getLogsFitting();
        } else if (activeTab === 'feed') {
            getLogsFeed();
        }
    }, [currentPage, activeTab, withEmployees]);

    useEffect(() => {
        if (dateBegin === '' && dateEnd === '') {
            if (activeTab === 'recommendation') {
                getLogsRecommendation();
            } else if (activeTab === 'fitting') {
                getLogsFitting();
            } else if (activeTab === 'feed') {
                getLogsFeed();
            }
        }
    }, [dateBegin, dateEnd])

    return (
        <div>
            <div className={s.tabs}>
                <button
                    className={`${s.tab} ${activeTab === 'feed' ? s.activeTab : ''}`}
                    onClick={() => handleTabChange('feed')}
                >
                    Просмотры ленты
                </button>
                <button
                    className={`${s.tab} ${activeTab === 'recommendation' ? s.activeTab : ''}`}
                    onClick={() => handleTabChange('recommendation')}
                >
                    Коллажи с вещами из шкафа
                </button>
                <button
                    className={`${s.tab} ${activeTab === 'fitting' ? s.activeTab : ''}`}
                    onClick={() => handleTabChange('fitting')}
                >
                    Логи переодеваний
                </button>
            </div>

            <div className={s.filterBlock}>
                <div className={s.dateBlock}>
                    <div className={s.headerName}>Дата начала</div>
                    <input
                        value={dateBegin}
                        type="date"
                        onChange={e => setDateBegin(e.target.value)}
                        placeholder="Введите дату начала для фильтрации"
                        className={s.filterClothesFormUp}
                    />
                </div>
                <div className={s.dateBlock}>
                    <div className={s.headerName}>Дата окончания</div>
                    <input
                        value={dateEnd}
                        type="date"
                        onChange={e => setDateEnd(e.target.value)}
                        placeholder="Введите дату начала для фильтрации"
                        className={s.filterClothesFormUp}
                    />
                </div>
                <div className={s.btns}>
                    <Button type="button" className={s.searchFormButton} onClick={find}>Поиск</Button>
                    <Button type="button" className={s.searchFormButton} onClick={cleanForm}>Сбросить фильтр</Button>
                    <div className={s.archiveSelector}>
                        <div className={s.selector}>
                            <div
                                className={`${s.selectorItem} ${!withEmployees ? s.active : ''}`}
                                onClick={() => handleArchiveToggle(false)}
                            >
                                Не показывать сотрудников
                            </div>
                            <div
                                className={`${s.selectorItem} ${withEmployees ? s.active : ''}`}
                                onClick={() => handleArchiveToggle(true)}
                            >
                                показывать
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className={s.loading}>
                    <Loader/>
                </div>
            ) : activeTab === 'recommendation' ? (
                logsRecommendation.length > 0 ? (
                    logsRecommendation.map((log, index) => (
                        <LogsRecommendItemList key={`recommend-${index}`} log={log} />
                    ))
                ) : (
                    <div className={s.noResults}>Нет данных для отображения</div>
                )
            ) : activeTab === 'fitting' ? (
                logsFitting.length > 0 ? (
                    logsFitting.map((log, index) => (
                        <LogsFittingItemList key={`fitting-${index}`} log={log} />
                    ))
                ) : (
                    <div className={s.noResults}>Нет данных для отображения</div>
                )
            ) : (
                logsFeed.length > 0 ? (
                    <div className={s.gridContainer}>
                        {logsFeed.map((log, index) => (
                            <LogsFeedItemList key={`feed-${index}`} log={log} />
                        ))}
                    </div>
                ) : (
                    <div className={s.noResults}>Нет данных для отображения</div>
                )
            )}

            {activeTab === 'recommendation' ? (
                allRecommendationItems > 0 && (
                    <PaginationControl
                        page={currentPage}
                        between={4}
                        total={allRecommendationItems}
                        limit={itemsPerPage}
                        changePage={(page) => {
                            paginate(page)
                        }}
                        ellipsis={1}
                    />
                )
            ) : activeTab === 'fitting' ? (
                allFittingItems > 0 && (
                    <PaginationControl
                        page={currentPage}
                        between={4}
                        total={allFittingItems}
                        limit={itemsPerPage}
                        changePage={(page) => {
                            paginate(page)
                        }}
                        ellipsis={1}
                    />
                )
            ) : (
                allFeedItems > 0 && (
                    <PaginationControl
                        page={currentPage}
                        between={4}
                        total={allFeedItems}
                        limit={itemsPerPage}
                        changePage={(page) => {
                            paginate(page)
                        }}
                        ellipsis={1}
                    />
                )
            )}
        </div>
    );
}